import {
  IGeocodeFeature,
  IMapboxRetrievedFeatureProperties,
  IMapboxRetrievedSuggestion,
  IMapboxSuggestion,
} from '@/services/types/mapbox/mapboxApi';
import { IQuoteDeliveryLocation } from '@/services/types/quote/IDeliveryLocation';

export const itemizeAddress = (
  address: IQuoteDeliveryLocation &
    (IGeocodeFeature | IMapboxSuggestion | IMapboxRetrievedFeatureProperties),
  retrievedSuggestion?: IMapboxRetrievedSuggestion,
) => {
  let latLong = null;
  let searchNameLocation = null;
  let fullSearchNameLocation = null;

  if (address === undefined) return {};

  const isAddressString = typeof address === 'string';
  if (isAddressString) {
    return {};
  }

  if ('center' in address && address.center) {
    latLong = {
      lng: address.center[0],
      lat: address.center[1],
    };
  } else if (address?.lat && address?.lng) {
    latLong = {
      lng: address?.lng,
      lat: address?.lat,
    };
  } else if (retrievedSuggestion && retrievedSuggestion.features[0]) {
    latLong = {
      lng: retrievedSuggestion.features[0].geometry.coordinates[0],
      lat: retrievedSuggestion.features[0].geometry.coordinates[1],
    };
  }

  if ('name' in address) {
    searchNameLocation = address.name;
  }

  if ('name_preferred' in address || 'place_formatted' in address) {
    fullSearchNameLocation =
      (address as IMapboxSuggestion).name_preferred || address.place_formatted;
  }

  const splitContext = (() => {
    if (!address.context) return null;

    // Handle IGeocodeFeature context (context is an array of features)
    if (Array.isArray(address.context)) {
      return address.context.reduce<Record<string, string>>((acc, cur) => {
        // @ts-expect-error fixable: unchecked index access
        acc[cur.id.split('.')[0]] = cur.text;
        return acc;
      }, {});
    }

    // Handle IMapboxSuggestion context
    const context = address.context;
    const result: Record<string, string> = {};

    if (context.country) {
      result.country = context.country.name;
    }
    if (context.region) {
      result.region = context.region.name;
    }
    if (context.district) {
      result.district = context.district.name;
    }
    if (context.postcode) {
      result.postcode = context.postcode.name;
    }
    if (context.place) {
      result.place = context.place.name;
    }
    if (context.address) {
      result.address = context.address.name;
    }
    if (context.street) {
      result.street = context.street.name;
    }

    return result;
  })();

  const street = [
    ...('place_name' in address ? [address.place_name?.split(',')[0]] : []),
    ...('place_name' in address ? [address?.properties?.address] : []),
    ...('name_preferred' in address ? [address.name_preferred?.split(',')[0]] : []),
    ...('name' in address ? [address.name?.split(',')[0]] : []),
    ...('place_formatted' in address ? [address.place_formatted?.split(',')[0]] : []),
  ].find(Boolean);

  const itemizedAddress: IQuoteDeliveryLocation = {
    ...latLong,
    formattedAddress: 'place_name' in address ? address.place_name : address.place_formatted,
    street: splitContext?.address || street,
    city: splitContext?.place,
    state: splitContext?.region,
    country: splitContext?.country,
    zip: splitContext?.postcode,
    location: address,
    ...(searchNameLocation && { search_name_location: searchNameLocation }),
    ...(fullSearchNameLocation && { full_search_name_location: fullSearchNameLocation }),
  };
  return itemizedAddress;
};
